/**
 * ┌──────────────────────────────────────────────────────────────────┐
 * │  CENTRALIZED SEO CONFIG — Single source of truth                │
 * │  Edit titles, descriptions, and canonical URLs here.            │
 * │  All pages + the build-time prerender plugin read from this.    │
 * │                                                                 │
 * │  CTR-OPTIMIZED: Power words, numbers, brackets, year, emojis.  │
 * │  Target: >5% CTR from current 2.4% baseline.                   │
 * └──────────────────────────────────────────────────────────────────┘
 */

export const SITE_URL = "https://www.drshimaaanwer.com";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.webp`;

export interface PageSeo {
  /** Page title — keep under 60 characters for Google */
  title: string;
  /** Meta description — keep under 160 characters */
  description: string;
  /** Full canonical URL */
  canonical: string;
  /** OG type (default: "website") */
  ogType?: string;
  /** Set true to add noindex,nofollow */
  noindex?: boolean;
  /** Custom OG image URL (defaults to site OG image) */
  ogImage?: string;
  /** Breadcrumb trail for JSON-LD */
  breadcrumbs?: { name: string; url?: string }[];
}

/**
 * SEO metadata for every static page.
 * Blog posts get their metadata dynamically from the database.
 *
 * CTR RULES APPLIED:
 * 1. Title: Number + Power word + Keyword + [Bracket modifier]
 * 2. Description: Emoji hook → Benefit → Social proof → CTA with →
 * 3. Year signals freshness for YMYL topics
 * 4. Arabic emotional triggers: مجاناً, بدون حرمان, نتائج مضمونة
 */
export const PAGE_SEO: Record<string, PageSeo> = {
  /* ─────────────── الصفحة الرئيسية ─────────────── */
  home: {
    title: "د. شيماء أنور — دكتورة تغذية علاجية أونلاين [جلسة ٢٠٢٦]",
    description:
      "⭐ د. شيماء أنور — استشاري تغذية علاجية وطب نمط الحياة. +٥٠٠ عميل نزّلوا ١٥ كيلو بدون حرمان. احجز جلستك المجانية واحصل على نظام مخصص ١٠٠٪ →",
    canonical: `${SITE_URL}/`,
    breadcrumbs: [
      { name: "الرئيسية", url: `${SITE_URL}/` },
    ],
  },

  /* ─────────────── الصفحة السعودية ─────────────── */
  sa: {
    title: "د. شيماء أنور — دكتورة تغذية أونلاين السعودية [جلسة ٢٠٢٦]",
    description:
      "🇸🇦 +٥٠٠ عميل سعودي نزّلوا ١٥ كيلو بدون حرمان! دكتورة تغذية علاجية أونلاين للرياض وجدة والدمام — احجز جلستك بالريال الحين →",
    canonical: `${SITE_URL}/sa`,
    breadcrumbs: [
      { name: "الرئيسية", url: `${SITE_URL}/` },
      { name: "السعودية" },
    ],
  },

  /* ─────────────── عن الدكتورة ─────────────── */
  about: {
    title: "د. شيماء أنور | استشاري تغذية علاجية وطب نمط الحياة (السيرة)",
    description:
      "🏆 د. شيماء أنور — أستاذ صحة عامة بكلية الطب واستشاري تغذية علاجية بخبرة +١٥ سنة. ساعدت +٥٠٠ عميل في مصر والخليج. تعرّف على قصتها ومنهجها →",
    canonical: `${SITE_URL}/about`,
    breadcrumbs: [
      { name: "الرئيسية", url: `${SITE_URL}/` },
      { name: "عن د. شيماء أنور" },
    ],
  },

  /* ─────────────── الخدمات ─────────────── */
  services: {
    title: "باقات تغذية علاجية (٣ خيارات) | أسعار ٢٠٢٦ + جلسة مجانية",
    description:
      "💰 قارن باقات البداية والتحول والنخبة — نظام غذائي يومي + متابعة أسبوعية مع د. شيماء أنور. تبدأ من ٢,٤٥٠ ج.م. اختار باقتك الآن →",
    canonical: `${SITE_URL}/services`,
    breadcrumbs: [
      { name: "الرئيسية", url: `${SITE_URL}/` },
      { name: "الخدمات والباقات" },
    ],
  },

  /* ─────────────── المدونة ─────────────── */
  blog: {
    title: "مدونة التغذية العلاجية | نصائح مجانية ٢٠٢٦ — د. شيماء أنور",
    description:
      "📚 +٣٠ مقال علمي مجاني عن إنقاص الوزن، تكيس المبايض، الصيام المتقطع، والأكل العاطفي. نصائح عملية تقدر تطبقها النهاردة — اقرأ مجاناً ←",
    canonical: `${SITE_URL}/blog`,
    breadcrumbs: [
      { name: "الرئيسية", url: `${SITE_URL}/` },
      { name: "مدونة التغذية العلاجية" },
    ],
  },

  /* ─────────────── سياسة الخصوصية ─────────────── */
  privacyPolicy: {
    title: "سياسة الخصوصية | د. شيماء أنور",
    description:
      "سياسة الخصوصية لموقع د. شيماء أنور - تعرف على كيفية حماية بياناتك الشخصية.",
    canonical: `${SITE_URL}/privacy-policy`,
    noindex: true,
    breadcrumbs: [
      { name: "الرئيسية", url: `${SITE_URL}/` },
      { name: "سياسة الخصوصية" },
    ],
  },

  /* ─────────────── 404 ─────────────── */
  notFound: {
    title: "الصفحة غير موجودة | د. شيماء أنور",
    description: "الصفحة اللي بتدوّر عليها مش موجودة. ارجع للصفحة الرئيسية.",
    canonical: `${SITE_URL}/404`,
    noindex: true,
  },
};
